import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['totalGrossValue', 'totalNetValue', 'valuationList', 'emptyState', 'valuationItem']
  static values = { vatRate: Number }

  connect() {
    this._manualGrossEdit = false
    this._observeFirmSelection()
    this._observeManualGrossEdit()
    this._onChange = (event) => this._onFormChange(event)
    this.element.addEventListener('change', this._onChange)
    this.updateTotals()
  }

  disconnect() {
    this._observer?.disconnect()
    this.element.removeEventListener('change', this._onChange)
    this._grossVisibleInput?.removeEventListener('input', this._onManualGrossInput)
  }

  _onFormChange(event) {
    if (this._grossVisibleInput && event.target === this._grossVisibleInput) return

    this.updateTotals()
  }

  updateTotals() {
    let net = 0
    this.valuationItemTargets.forEach(item => {
      const checkbox = item.querySelector('input[type="checkbox"]')
      if (checkbox?.checked) {
        net += parseFloat(item.dataset.net || 0)
      }
    })
    const gross = +(net * this.vatRateValue).toFixed(2)

    if (this.hasTotalGrossValueTarget) this.totalGrossValueTarget.textContent = gross.toFixed(2)
    if (this.hasTotalNetValueTarget) this.totalNetValueTarget.textContent = net.toFixed(2)

    if (this._manualGrossEdit) return

    this._setTotalGross(gross > 0 ? gross.toFixed(2) : '')
  }

  _observeManualGrossEdit() {
    const hidden = this.element.querySelector('input[type="hidden"][name="invoice[total_gross]"]')
    const wrapper = hidden?.closest('[data-controller~="input"]')
    const visible = wrapper?.querySelector('[data-input-target="input"]')
    if (!visible) return

    this._grossVisibleInput = visible
    this._manualGrossEdit = (visible.value || '').trim() !== ''
    this._onManualGrossInput = (event) => {
      if (!event.isTrusted) return
      this._manualGrossEdit = true
    }
    visible.addEventListener('input', this._onManualGrossInput)
  }

  _setTotalGross(value) {
    const hidden = this.element.querySelector('input[type="hidden"][name="invoice[total_gross]"]')
    const wrapper = hidden?.closest('[data-controller~="input"]')
    const visible = wrapper?.querySelector('[data-input-target="input"]')
    if (!visible) return

    visible.value = value
    visible.dispatchEvent(new Event('input', { bubbles: true }))
  }

  _observeFirmSelection() {
    const hidden = this.element.querySelector('[data-single-select-field-component-with-search-target="hidden"]')
    if (!hidden) return

    this._firmHidden = hidden
    this._lastFirmId = this._resolveFirmId()

    this._observer = new MutationObserver(() => this._onFirmSelectionChange())
    this._observer.observe(hidden, { attributes: true, attributeFilter: ['value'] })
    hidden.addEventListener('change', () => this._onFirmSelectionChange())
  }

  _resolveFirmId() {
    return (this._firmHidden?.value || '').trim()
  }

  _onFirmSelectionChange() {
    const firmId = this._resolveFirmId()
    if (firmId === this._lastFirmId) return

    const previous = this._lastFirmId
    this._lastFirmId = firmId

    if (previous) this._resetFormFields()

    if (!firmId) {
      this._showEmptyState()
      return
    }

    this._fetchValuations(firmId)
  }

  _resetFormFields() {
    const invoiceNumber = this._inputByName('invoice[invoice_number]')
    if (invoiceNumber) invoiceNumber.value = ''

    this._manualGrossEdit = false
    this._setTotalGross('')

    const date = this._inputByName('invoice[date]', { excludeHidden: true })
    if (date) {
      date.value = ''
      date.dispatchEvent(new Event('input', { bubbles: true }))
      date.dispatchEvent(new Event('change', { bubbles: true }))
    }

    const file = this.element.querySelector('input[type="file"][name="invoice[file]"]')
    if (file) {
      file.value = ''
      const wrapper = file.closest('[data-controller~="file-upload"]')
      wrapper?.querySelectorAll('.attachment').forEach(el => el.remove())
    }
  }

  _inputByName(name, { excludeHidden = false } = {}) {
    const inputs = this.element.querySelectorAll(`input[name="${name}"]`)
    for (const input of inputs) {
      if (excludeHidden && input.type === 'hidden') continue
      return input
    }
    return null
  }

  _showEmptyState() {
    if (this.hasValuationListTarget) {
      this.valuationListTarget.innerHTML = ''
      this.valuationListTarget.classList.add('hidden')
    }
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove('hidden')
    this.updateTotals()
  }

  async _fetchValuations(firmId) {
    try {
      const response = await fetch(`/valuation_firms/${firmId}/outstanding_invoices`, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin'
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const html = await response.text()
      if (this.hasValuationListTarget) {
        this.valuationListTarget.innerHTML = html
        this.valuationListTarget.classList.remove('hidden')
      }
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add('hidden')
      this.updateTotals()
    } catch (error) {
      if (this.hasValuationListTarget) {
        this.valuationListTarget.innerHTML = '<p class="text-paragraph-m text-red-1000">Failed to load valuations. Please try again.</p>'
        this.valuationListTarget.classList.remove('hidden')
      }
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add('hidden')
    }
  }
}
